import { Button, Card, CardBody, Eyebrow, Input, Label } from '@/shared/components';
import type { StripeSettingsResponse, UpdateStripeSettingsRequest } from '@/shared/api';

interface StripeCredentialsCardProps {
  form: UpdateStripeSettingsRequest;
  settings: StripeSettingsResponse;
  saving: boolean;
  checking: boolean;
  onChange: (update: Partial<UpdateStripeSettingsRequest>) => void;
  onSave: () => void;
  onCheck: () => void;
}

export function StripeCredentialsCard({
  form,
  settings,
  saving,
  checking,
  onChange,
  onSave,
  onCheck,
}: StripeCredentialsCardProps) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <Card>
        <CardBody className="p-6">
          <Eyebrow variant="muted">Zugangsdaten</Eyebrow>
          <p className="text-ink-soft mt-1 text-sm">
            Bereits gespeicherte Secrets werden nie angezeigt. Lassen Sie ein Secret-Feld leer, um
            den vorhandenen Wert beizubehalten.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField
              label="Kontobezeichnung"
              value={form.accountLabel ?? ''}
              onChange={(accountLabel) => onChange({ accountLabel: accountLabel || null })}
            />
            <TextField
              label="Publishable Key"
              value={form.publishableKey}
              onChange={(publishableKey) => onChange({ publishableKey })}
            />
            <TextField
              label="Secret Key"
              type="password"
              placeholder={settings.secretKeyConfigured ? 'Gespeichert – leer lassen' : 'sk_…'}
              value={form.secretKey}
              onChange={(secretKey) => onChange({ secretKey })}
            />
            <TextField
              label="Webhook Signing Secret"
              type="password"
              placeholder={
                settings.webhookSigningSecretConfigured ? 'Gespeichert – leer lassen' : 'whsec_…'
              }
              value={form.webhookSigningSecret}
              onChange={(webhookSigningSecret) => onChange({ webhookSigningSecret })}
            />
          </div>
        </CardBody>
      </Card>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" loading={checking} onClick={onCheck}>
          Verbindung prüfen
        </Button>
        <Button type="submit" variant="brand" loading={saving}>
          Konfiguration speichern
        </Button>
      </div>
    </form>
  );
}

function TextField({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
}: {
  label: string;
  type?: 'text' | 'password';
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputId = `stripe-${label.toLowerCase().replaceAll(' ', '-')}`;

  return (
    <div>
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        spellCheck={false}
      />
    </div>
  );
}
